/** A JSON-compatible value: what every record value becomes before the console call (R12). */
export type NormalizedValue = string | number | boolean | null | NormalizedValue[] | { [key: string]: NormalizedValue }

export type SerializedError = {
  name: string
  message: string
  stack?: string
  cause?: SerializedError | NormalizedValue
  /** An entry is `"[Circular]"` when the aggregate contains an error already on the path. */
  errors?: (SerializedError | NormalizedValue)[]
}

/** The hops already taken on the current path: `value` counts containers, `cause` counts `cause` and `errors` hops. */
export type Depth = { value: number; cause: number }

/**
 * Returns `undefined` for a value the record drops (R12). `depth` is the nesting of `value`; the record's own keys are
 * at `{ value: 1, cause: 0 }`. A `toJSON` result counts as one container hop, so a `toJSON` that returns another object
 * with a `toJSON` stops at {@link MAX_VALUE_DEPTH} rather than recursing until the stack overflows. `ancestors` holds
 * the containers on the current path, so a cycle becomes `"[Circular]"`. A throwing getter, `toJSON`, or proxy trap
 * propagates, and `buildRecord` turns it into `serializationFailed` (R11).
 */
export function normalizeValue(
  value: unknown,
  depth: Depth,
  ancestors: Set<object>
): NormalizedValue | SerializedError | undefined {
  if (typeof value === 'string') {
    return truncate(value)
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'bigint') {
    return truncate(value.toString())
  }
  if (typeof value !== 'object') {
    // `undefined`, a function, or a symbol.
    return undefined
  }
  if (value === null) {
    return null
  }
  if (ancestors.has(value)) {
    return '[Circular]'
  }
  if (isError(value)) {
    return serializeError(value, depth, ancestors)
  }
  if (depth.value > MAX_VALUE_DEPTH) {
    return '[MaxDepth]'
  }
  if (hasToJson(value)) {
    // `Date` and `Temporal` values take this path: `Date#toJSON` gives ISO 8601, or `null` for an invalid date.
    return normalizeValue(value.toJSON(), intoContainer(depth), ancestors)
  }
  ancestors.add(value)
  try {
    return normalizeContainer(value, depth, ancestors)
  } finally {
    ancestors.delete(value)
  }
}

/**
 * `{ name, message, stack, cause? }` with `cause` and the `errors` of an `AggregateError` recursed while `depth.cause`
 * is below {@link MAX_CAUSE_DEPTH} (R4). A non-`Error` cause or entry is normalised as a value from the same `depth`,
 * and a nested error continues the same cause count. A path therefore takes at most {@link MAX_CAUSE_DEPTH} error hops
 * and {@link MAX_VALUE_DEPTH} container hops, whatever order an error and a plain object alternate in. The error joins
 * `ancestors` while its members are serialised, so an error that contains itself yields `"[Circular]"` at the second
 * visit.
 */
export function serializeError(error: ErrorLike, depth: Depth, ancestors: Set<object>): SerializedError {
  const message = scrubQueryParams(error.message)
  const serialized: SerializedError = {
    name: truncate(error.name),
    message: truncate(message),
  }
  if (typeof error.stack === 'string') {
    serialized.stack = truncate(scrubStack({ stack: error.stack, message: error.message, scrubbed: message }))
  }
  if (depth.cause >= MAX_CAUSE_DEPTH) {
    return serialized
  }
  const memberDepth = intoCause(depth)
  ancestors.add(error)
  try {
    if (error.cause !== undefined) {
      const cause = serializeMember(error.cause, memberDepth, ancestors)
      if (cause !== undefined) {
        serialized.cause = cause
      }
    }
    if (error instanceof AggregateError) {
      serialized.errors = error.errors.map((entry: unknown) => serializeMember(entry, memberDepth, ancestors) ?? null)
    }
  } finally {
    ancestors.delete(error)
  }
  return serialized
}

/** `instanceof` for the common case; the tag check covers an `Error` from another realm. */
export function isError(value: unknown): value is ErrorLike {
  return value instanceof Error || Object.prototype.toString.call(value) === '[object Error]'
}

/** Cuts a string to {@link MAX_STRING_LENGTH} UTF-16 code units with a marker (R13). */
export function truncate(value: string): string {
  return value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}…[truncated]` : value
}

/** Longest string value kept in a record, in UTF-16 code units (R13). */
export const MAX_STRING_LENGTH = 16_384

/** Deepest container kept when a value is normalised (R12). The record's own keys are at depth 1. */
export const MAX_VALUE_DEPTH = 8

/** How many nested `cause` values, or levels of `AggregateError#errors`, an error keeps (R4). */
export const MAX_CAUSE_DEPTH = 5

type ErrorLike = Error & { cause?: unknown }

/**
 * `scrubSensitiveErrorData.ts` in `@district-core/observability` applies this same rule to Sentry exception values.
 * That package depends on this one, so the rule is repeated here rather than imported. Drizzle appends the bound
 * parameter values to its message after `\nparams:`, and those values are user data.
 */
const DRIZZLE_MESSAGE_PARAMS = /\nparams: [\s\S]*$/u

/** A `cause` or an `errors` entry: an error on the current path is a cycle, an `Error` recurses, a value normalises. */
function serializeMember(
  member: unknown,
  depth: Depth,
  ancestors: Set<object>
): SerializedError | NormalizedValue | undefined {
  if (typeof member === 'object' && member !== null && ancestors.has(member)) {
    return '[Circular]'
  }
  return isError(member) ? serializeError(member, depth, ancestors) : normalizeValue(member, depth, ancestors)
}

/** One container hop. The cause count does not change, so alternating containers and errors cannot bypass either limit. */
function intoContainer(depth: Depth): Depth {
  return { value: depth.value + 1, cause: depth.cause }
}

/**
 * One `cause` or `errors` hop. The container count does not change, so alternating containers and errors cannot bypass
 * either limit.
 */
function intoCause(depth: Depth): Depth {
  return { value: depth.value, cause: depth.cause + 1 }
}

/**
 * A V8 stack begins with `${name}: ${message}`, so the bound parameters scrubbed from the message would survive in the
 * stack. Only the message text is replaced; the frames stay. A replacer function keeps a `$` in the scrubbed message
 * literal.
 */
function scrubStack({ stack, message, scrubbed }: { stack: string; message: string; scrubbed: string }): string {
  return message === scrubbed || message === '' ? stack : stack.replace(message, () => scrubbed)
}

function normalizeContainer(container: object, depth: Depth, ancestors: Set<object>): NormalizedValue {
  if (Array.isArray(container) || container instanceof Set) {
    const items: NormalizedValue[] = []
    for (const item of container) {
      const normalized = normalizeValue(item, intoContainer(depth), ancestors)
      if (normalized !== undefined) {
        items.push(normalized)
      }
    }
    return items
  }
  const entries: Iterable<[unknown, unknown]> =
    container instanceof Map ? container.entries() : Object.entries(container)
  const result: [string, NormalizedValue][] = []
  for (const [key, item] of entries) {
    const normalized = normalizeValue(item, intoContainer(depth), ancestors)
    if (normalized !== undefined) {
      result.push([String(key), normalized])
    }
  }
  // `Object.fromEntries` defines data properties, so a key named `__proto__` stays a key (R12).
  return Object.fromEntries(result)
}

function scrubQueryParams(message: string): string {
  return message.replace(DRIZZLE_MESSAGE_PARAMS, '\nparams: [redacted]')
}

function hasToJson(value: object): value is { toJSON: () => unknown } {
  return 'toJSON' in value && typeof value.toJSON === 'function'
}
